import { useRef } from 'react';
import { motion, useInView, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FileText, 
  Scan, 
  AlertCircle, 
  BarChart3, 
  Target, 
  Gauge,
  BookX,
  TrendingUp,
  ArrowRight
} from 'lucide-react';
import MobileSwipeCarousel from '../../components/mobile/MobileSwipeCarousel';
import MobileAccordion from '../../components/mobile/MobileAccordion';
import AnimatedBackground from '../../components/AnimatedBackground';

// Hero capabilities - top 3 large cards
const heroCapabilities = [
  {
    id: 'auto-scan',
    icon: Scan,
    title: 'Auto Scan & Evaluation',
    description: 'Upload answer sheets and get instant AI-powered evaluation matching examiner standards. Works with handwritten and printed papers.',
    iconColor: 'text-blue-500'
  },
  {
    id: 'error-classification',
    icon: AlertCircle,
    title: 'Error Classification',
    description: 'Categorize mistakes as conceptual, careless, or strategic to understand root causes. Know exactly why marks were lost.',
    iconColor: 'text-red-500'
  },
  {
    id: 'exam-paper-generation',
    icon: FileText,
    title: 'Exam Paper Generation',
    description: 'Generate board-aligned question papers with varying difficulty levels and topic distribution. Perfect for practice tests.',
    iconColor: 'text-primary'
  }
];

// More capabilities - smaller cards
const moreCapabilities = [
  {
    icon: BarChart3,
    title: 'Skill-Level Diagnosis',
    description: 'Map performance to specific skills and competencies'
  },
  {
    icon: Target,
    title: 'Strategy Analysis',
    description: 'Analyze time allocation and question selection patterns'
  },
  {
    icon: Gauge,
    title: 'Confidence Scoring',
    description: 'Measure answer confidence to identify uncertainty areas'
  },
  {
    icon: BookX,
    title: 'Wrong Answer Notebook',
    description: 'Auto-generate revision material from incorrect answers'
  },
  {
    icon: TrendingUp,
    title: 'Progress Tracking',
    description: 'Monitor improvement over time with detailed analytics'
  }
];

// Mobile compact capability card
function MobileCapabilityCard({ capability }) {
  const Icon = capability.icon;

  return (
    <Link to={`/capabilities/${capability.id}`}>
      <div className="bg-card rounded-xl border border-border p-6 h-full">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-xl bg-muted/50 flex items-center justify-center mb-4">
            <Icon className={`w-8 h-8 ${capability.iconColor}`} />
          </div>
          <h3 className="text-lg font-semibold mb-2">{capability.title}</h3>
          <p className="text-sm text-muted-foreground mb-4">{capability.description}</p>
          <span className="text-sm text-primary font-medium flex items-center gap-1">
            Learn more <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </Link>
  );
}

// Hero capability card - 3D Tilt Effect Version - SMALLER for PC
function HeroCapabilityCard({ capability, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const Icon = capability.icon;

  // Mouse tracking for 3D tilt
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  
  const rotateX = useSpring(useTransform(y, [-100, 100], [8, -8]), {
    stiffness: 300,
    damping: 30
  });
  const rotateY = useSpring(useTransform(x, [-100, 100], [-8, 8]), {
    stiffness: 300,
    damping: 30
  });

  const handleMouseMove = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    x.set(e.clientX - centerX);
    y.set(e.clientY - centerY);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <Link to={`/capabilities/${capability.id}`}>
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 40, scale: 0.95 }}
        animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
        transition={{ delay: index * 0.12, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
        style={{
          rotateX,
          rotateY,
          transformStyle: 'preserve-3d',
          perspective: 1000
        }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className="group relative bg-card rounded-xl border border-border p-6 hover:border-primary/40 transition-all duration-500 cursor-pointer overflow-hidden h-full"
      >
        {/* Reduced glow effect on hover - opacity from 20 to 10 */}
        <motion.div
          className="absolute -inset-px rounded-xl bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 opacity-0 blur-sm group-hover:opacity-100 transition-opacity duration-500"
          style={{ transform: 'translateZ(-10px)' }}
        />

        {/* Softened Background Gradient */}
        <motion.div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
          style={{
            background: 'linear-gradient(135deg, transparent 0%, rgba(255,255,255,0.02) 50%, transparent 100%)'
          }}
        />
        
        <div className="relative" style={{ transform: 'translateZ(20px)' }}>
          <div className="w-14 h-14 rounded-xl bg-muted/50 flex items-center justify-center mb-4 group-hover:bg-muted transition-colors duration-300">
            <Icon className={`w-7 h-7 ${capability.iconColor}`} />
          </div>
          
          <h3 className="text-lg font-semibold mb-2 group-hover:text-primary transition-colors">
            {capability.title}
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed mb-4">
            {capability.description}
          </p>

          <motion.div
            className="flex items-center gap-2 text-primary opacity-0 group-hover:opacity-100 transition-opacity"
            initial={{ x: -10 }}
            whileHover={{ x: 0 }}
          >
            <span className="text-sm font-medium">Learn more</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </motion.div>
        </div>
      </motion.div>
    </Link>
  );
}

// Small capability card
function SmallCapabilityCard({ capability, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-30px" });
  const Icon = capability.icon;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.1, duration: 0.4 }}
      className="group flex items-start gap-5 p-6 rounded-xl bg-card border border-border hover:border-primary/30 transition-all duration-300 cursor-pointer"
    >
      <div className="p-4 rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-colors flex-shrink-0">
        <Icon className="w-7 h-7 text-primary" />
      </div>
      <div>
        <h4 className="font-semibold text-xl mb-2 group-hover:text-primary transition-colors">
          {capability.title}
        </h4>
        <p className="text-base text-muted-foreground">{capability.description}</p>
      </div>
    </motion.div>
  );
}

export default function CapabilitiesSection() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  return (
    <section 
      ref={sectionRef}
      id="capabilities"
      data-testid="capabilities-section"
      className="relative py-20 md:py-28 bg-card/50 overflow-hidden"
    >
      {/* Subtle Background Animation */}
      <div className="absolute inset-0 opacity-20">
        <AnimatedBackground variant="subtle" />
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-12 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-8 md:mb-20"
        >
          {/* Header Badge - Removed 'whileHover scale' animation */}
          <div className="inline-flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 rounded-full bg-primary/10 border border-primary/20 mb-4 md:mb-8">
            <BarChart3 className="w-5 h-5 md:w-6 md:h-6 text-primary" />
            <span className="text-sm md:text-lg font-medium text-primary">What We Do</span>
          </div>
          
          <h2 className="text-2xl md:text-5xl lg:text-7xl font-bold tracking-tight mb-3 md:mb-8">
            <span className="hidden md:inline">
              Everything you need to
              <br />
              <motion.span 
                className="text-primary"
                animate={{ opacity: [1, 0.7, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                diagnose exam performance
              </motion.span>
            </span>
            <span className="md:hidden">
              Core <span className="text-primary">Capabilities</span>
            </span>
          </h2>
          
          <p className="text-sm md:text-xl lg:text-2xl text-muted-foreground max-w-3xl mx-auto hidden md:block">
            A comprehensive suite of AI-powered tools designed to provide actionable insights from every exam.
          </p>
        </motion.div>

        <div className="hidden md:grid md:grid-cols-3 gap-8 mb-16">
          {heroCapabilities.map((capability, index) => (
            <HeroCapabilityCard key={index} capability={capability} index={index} />
          ))}
        </div>

        <div className="md:hidden mb-8">
          <MobileSwipeCarousel
            items={heroCapabilities}
            renderItem={(capability) => (
              <MobileCapabilityCard capability={capability} />
            )}
          />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          <div className="hidden md:block">
            <div className="flex items-center gap-4 mb-8">
              <div className="h-px flex-1 bg-border" />
              <span className="text-base font-medium text-muted-foreground px-4">More Capabilities</span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {moreCapabilities.map((capability, index) => (
                <SmallCapabilityCard key={index} capability={capability} index={index} />
              ))}
            </div>
          </div>

          <div className="md:hidden">
            <MobileAccordion trigger={`See all capabilities (${moreCapabilities.length} more)`} defaultExpanded={false}>
              <div className="space-y-3">
                {moreCapabilities.map((capability, index) => {
                  const Icon = capability.icon;
                  return (
                    <div key={index} className="flex items-start gap-3 py-2">
                      <div className="p-2 rounded-lg bg-primary/10 flex-shrink-0">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm mb-1">{capability.title}</h4>
                        <p className="text-xs text-muted-foreground">{capability.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </MobileAccordion>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
